import {ChangeDetectionStrategy, Component, forwardRef, Input, OnDestroy, OnInit} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormControl} from '@angular/forms';
import {AllowedCountryResponse} from "@services/authentication/model/allowedCountryResponse";
import {DictionariesService} from "@shared/services/dictionaries.service";
import {Subject, takeUntil} from "rxjs";
import {map} from "rxjs/operators";
import * as mainActions from "@main/ngrx/actions/main.actions";
import {Store} from "@ngrx/store";
import {Reducers} from "@app/reducers";
import {FloatLabelType, MatFormFieldAppearance} from '@angular/material/form-field';

@Component({
    selector: 'app-phone-number-field',
    templateUrl: './phone-number-field.component.html',
    styleUrls: ['./phone-number-field.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PhoneNumberFieldComponent),
            multi: true
        }
    ],
    standalone: false
})
export class PhoneNumberFieldComponent implements ControlValueAccessor, OnInit, OnDestroy {
    phone = new UntypedFormControl('');
    country = new UntypedFormControl('');
    public countries: AllowedCountryResponse[];
    public prefix: string;
    public code: string;
    public mask: string;
    @Input() withCountry: boolean;
    @Input() appearance: MatFormFieldAppearance = 'fill';
    @Input() floatLabel: FloatLabelType = 'always';
    @Input() fieldClass = 'no-padding full-width';
    @Input() label = '';
    @Input() autoFocus = false;
    @Input() callAllowed = false;
    /** Subject to destroy Subscriptions */
    private destroyed$ = new Subject<void>();

    constructor(
        private dictionaryApi: DictionariesService,
        private store$: Store<Reducers>
    ) {
    }

    private _value: any;

    get value() {
        return this._value;
    }

    @Input()
    set value(val) {
        this._value = val;
        this.onChange(this._value);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onChange(_: any) {
    }

    onTouch = () => {
    };

    ngOnInit(): void {
        if (this.withCountry) {
            this.dictionaryApi.getAllowedCountries().pipe(
                takeUntil(this.destroyed$),
                map((res: AllowedCountryResponse[]) => {
                    this.countries = res;
                    this.countrySelected(res.find(el => el.code == '+380') ? res.find(el => el.code == '+380') : res[0]);
                    this.country.setValue(res.find(el => el.code == '+380') ? res.find(el => el.code == '+380') : res[0]);
                })
            ).subscribe();
        } else {
            this.prefix = '+38 ';
            this.mask = '(000) 000-00-00';
        }
    }

    writeValue(value: any) {
        this.phone.setValue(value);
        this.onChange(value);
    }

    registerOnChange(fn: any) {
        this.onChange = fn;
    }

    registerOnTouched(fn: any) {
        this.onTouch = fn;
    }

    /** Take only last 10 digits from the phone clipboard data on Ctrl+v event */
    onInputPaste(clipboardData: string | undefined) {
        if (this.withCountry) {
            return;
        } else {
            if (clipboardData) {
                const hasNumberReg = /\d/;
                const hasNumber = hasNumberReg.test(clipboardData);
                if (hasNumber) {
                    const trimmedValue = clipboardData.replace(/\D/g, '');
                    let tenDigitsFromTheEnd = trimmedValue.slice(-10);

                    if (tenDigitsFromTheEnd && tenDigitsFromTheEnd.length === 9) {
                        tenDigitsFromTheEnd = `0${tenDigitsFromTheEnd}`;
                    }

                    if (tenDigitsFromTheEnd && tenDigitsFromTheEnd.length === 10) {
                        this.phone.setValue(tenDigitsFromTheEnd);
                        this.onChange(tenDigitsFromTheEnd);
                    }
                }
            }
        }
    }

    countrySelected(value: any) {
        if (value.code && value.maskAndroid) {
            this.prefix = '';
            this.mask = value.maskAndroid.replaceAll('#', '0');
            this.code = value.code;
            this.numberChanged();
        }
    }

    numberChanged() {
        const number = this.withCountry && this.code
            ? `${this.code}${this.phone.value}`
            : this.phone.value;
        // @ts-ignore
        this.onChange(number.replaceAll(' ', ''));
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    call() {
        if (this.phone.value) {
            this.store$.dispatch(new mainActions.ProceedCall({command: 'call', number: this.phone.value}));
        }
    }
}
